import React, { useEffect, useMemo, useCallback, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Features } from "@/features";
import type { AppRoute, FeatureModule } from "@/shared/types/routing";

import NotFoundPage from "@/routing/not-found";
import Layout from "@/shared/layout/layout";
import AuthGuard from "@/routing/AuthGuard";
import GuestGuard from "@/routing/GuestGuard";
import ErrorBoundary from "@/shared/ui/error-boundary";
import ErrorFallback from "@/shared/components/error-fallback";
/**
 * Flatten all feature routes once at module level.
 * This is stable across renders since Features doesn't change.
 */
const allFeatureRoutes: AppRoute[] = _.flatMap(
  Features as FeatureModule[],
  (feature) => feature.routes || []
);

const AppRouter: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation('common');

  // Memoize route lookup for title updates
  const currentPageTitle = useMemo(() => {
    const currentPage = allFeatureRoutes.find(
      (page) => page.path === location.pathname || location.pathname.startsWith(page.path + "/")
    );
    return currentPage?.meta?.label ? t(currentPage.meta.label) : t('app.name');
  }, [location.pathname, t]);

  useEffect(() => {
    document.title = currentPageTitle;
  }, [currentPageTitle]);

  // Memoized route renderer with proper types
  const renderRoute = useCallback((route: AppRoute, isNested: boolean = false): React.ReactNode => {
    const element = route.element || null;

    // Auth logic: requiresAuth defaults to true (protected)
    const requiresAuth = route.meta?.requiresAuth !== false;
    const allowGuestAndAuth = route.meta?.allowGuestAndAuth === true;

    // Only wrap top-level routes in Layout. Child routes rendered inside Outlet don't need another Layout.
    const noSidebar = route.meta?.noSidebar ?? (allowGuestAndAuth ? true : !requiresAuth);
    const content = !isNested ? (
      <Layout component={element as React.ReactElement} noSidebar={noSidebar} />
    ) : (
      element
    );

    // Wrap in appropriate guard based on auth requirements
    // - allowGuestAndAuth: true → no guard (both guest and authenticated can access)
    // - requiresAuth: true → AuthGuard (redirect unauthenticated to login)
    // - requiresAuth: false → GuestGuard (redirect authenticated to dashboard)
    const guardedContent = allowGuestAndAuth ? (
      content
    ) : requiresAuth ? (
      <AuthGuard>
        {content}
      </AuthGuard>
    ) : (
      <GuestGuard>
        {content}
      </GuestGuard>
    );

    // Wrap with ErrorBoundary and Suspense
    const wrappedElement = (
      <ErrorBoundary
        onError={(error, errorInfo) => {
          if (globalThis.logger) {
            globalThis.logger.error(`Route error in ${route.path}:`, {
              error: error.message,
              componentStack: errorInfo.componentStack,
              route: route.path,
            });
          }
        }}
        fallback={
          <div className="min-h-screen flex items-center justify-center p-4">
            <ErrorFallback
              title={t('errors.errorTitle')}
              message={t('errors.errorPageDesc')}
              onRetry={() => window.location.reload()}
            />
          </div>
        }
      >
        {/* fallback=null avoids a second full-screen loader after bootstrap (prevents blink) */}
        <Suspense fallback={null}>
          {guardedContent}
        </Suspense>
      </ErrorBoundary>
    );

    // React Router v6: index routes can't have children
    if (route.index === true) {
      return (
        <Route
          key={route.path || 'index'}
          index
          element={wrappedElement}
        />
      );
    }

    return (
      <Route
        key={route.path || 'no-path'}
        path={route.path}
        element={wrappedElement}
      >
        {route.children?.map((child: AppRoute) => renderRoute(child, true))}
      </Route>
    );
  }, [t]);

  // Memoize the rendered routes array
  const renderedRoutes = useMemo(
    () => allFeatureRoutes.map((route) => renderRoute(route)),
    [renderRoute]
  );

  return (
    <Routes>
      {renderedRoutes}

      <Route
        path="*"
        element={
          <ErrorBoundary
            fallback={
              <div className="min-h-screen flex items-center justify-center p-4">
                <ErrorFallback
                  title={t('errors.pageNotFound')}
                  message={t('errors.pageNotFoundDesc')}
                  onRetry={() => window.location.href = '/'}
                />
              </div>
            }
          >
            <Suspense fallback={null}>
              <Layout component={<NotFoundPage />} />
            </Suspense>
          </ErrorBoundary>
        }
      />
    </Routes>
  );
};

export default AppRouter;